import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Card, Col, Container, Row, Table, Button, Form, OverlayTrigger, Tooltip, Badge, Modal} from "react-bootstrap";
import { useParams } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Spinner from 'react-bootstrap/Spinner';
import NotyfContext from "../contexts/NotyfContext";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import DetailCardHeader from "../utils/DetailCardHeader";
import { DetailSection, DetailField } from "../utils/DetailParts";
import { cardOutlineStyle } from "../utils/TaskCardParts";
import { Key, File, Briefcase, ExternalLink, X, Check, Edit2, Trash2, Download, FileText } from "react-feather";
import Select from "react-select";

import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFilePdf,
  faFileCsv,
  faUserCheck,
  faUserEdit,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import { flattenSupervisingProviders, withGroupedSupervisors } from "../utils/supervisingProviders";

import DocumentList from "../utils/DocumentList"
import EditableDetail from "../utils/EditableDetail"
import LoginCredentialsModal from "../utils/LoginCredentialsModal"
import LinesOfBusinessModal from "../utils/LinesOfBusinessModal"
import ContactInfoPopover from "../utils/ContactInfoPopover";
import EditableExpires from "../utils/EditableExpires";
import EditableExpiresControls from "../utils/EditableExpiresControls";
import HubParticipationStatusTable from "../utils/HubParticipationStatusTable"
import FacilityParticipationStatusTable from "../utils/FacilityParticipationStatusTable";
import OpenTasks from "../utils/OpenTasks";
import { TaskSummaryRow, TaskCard, SortButton } from "../utils/TaskCardParts";
import EditableAddress from "../utils/EditableAddress";
import ReadOnlyAddress from "../utils/ReadOnlyAddress";
import PeerReferences from "../utils/PeerReferences";
import Trainings from "../utils/Training";
import WorkHistories from "../utils/WorkHistory";
import PastHospitalPrivilegesList from "../utils/PastHospitalPrivileges";
import StateLicenses from "../utils/StateLicenses";
import MalpracticeInsurances from "../utils/MalpracticeInsurance";
import BoardCertifications from "../utils/BoardCertification";
import OtherCertifications from "../utils/OtherCertifications";
import Immunizations from "../utils/Immunizations";
import AreasOfExpertise from "../utils/AreasOfExpertise";
import TreatmentModalities from "../utils/TreatmentModalities";
import Psvs from "../utils/Psv";
import Disclosures from "../utils/Disclosures";
import { CopyText, CopyIds } from "../components/header/HeaderParts";
import CopyToClipboardToggle from "../utils/CopyToClipboardToggle";

import rawCMECategories from "../utils/cmeCategories";

import ProviderHeader from "../components/provider/ProviderHeader";
import ProviderEditPanel from "../components/provider/ProviderEditPanel";
import MaritalStatusSelect from "../utils/MaritalStatusSelect";
import rawMaritalStatuses from "../utils/maritalStatuses";
import ParticipationStatusIcon from "../utils/ParticipationStatusIcon";
import ParticipationDocumentsModal from "../utils/ParticipationDocumentsModal"

import SexSelect from "../utils/SexSelect";
import rawSexTypes from "../utils/sexTypes";


const ProviderOnboardingUrl = ({providerId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);


  useEffect(() => {
    authenticatedFetch({
      path: "provider/onboarding/url/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [providerId])


  if (!error && isLoaded && data)
  {
    if (data.hasActiveOnboardingUrl)
    {
      return (
        <OverlayTrigger
          trigger={["hover", "focus"]}
          placement="bottom"
          overlay={<Tooltip>This provider is actively completing their onboarding. They have the ability to edit data from various sections of their profile, including peer references, work history, etc.</Tooltip>}
        >
          <span className="text-warning ms-2" style={{fontSize: "0.8em"}}>
            <FontAwesomeIcon icon={faUserEdit} />
          </span>
        </OverlayTrigger>
      );
    }
    if (data.hasRecentlyCompletedOnboardingUrl)
    {
      return (
        <OverlayTrigger
          trigger={["hover", "focus"]}
          placement="bottom"
          overlay={<Tooltip>Onboarding completed!</Tooltip>}
        >
          <span className="text-muted ms-2" style={{fontSize: "0.8em"}}>
            <FontAwesomeIcon icon={faUserCheck} />
          </span>
        </OverlayTrigger>
      );
    }
  }

  return <></>;
}

const ParticipationStatusToggle = ({ handleTinSelected, practices }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  let practiceList = practices && practices.length > 0
		&& practices.map((practice, i) => {
		return (
			<option key={i} value={practice.id}>{practice.tin}&nbsp;[{practice.name}]</option>
		)
	}, this);

  if (!isLoaded && practices && practices.length > 0)
  {
    // Fire for the initial selection
    setIsLoaded(true);
    handleTinSelected(practices[0].id);
  }
  
  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Label className="mb-0" style={{fontSize: '0.75rem', color: '#8a929a'}}>TIN</Form.Label>
      <Form.Select size="sm" onChange={(e) => {handleTinSelected(e.target.value, e)}} style={{width: 'auto', maxWidth: '280px'}}>
        {practiceList}
      </Form.Select>
    </div>
  );
}


const CarrierParticipation = ({ onSave, carrierId, providerId, practiceId, practiceName, providerName, carrierName, phone, fax, email, expires, effectiveDate, payorNumber, portal, active=true, progress=false, pending=false, pendingTimestamp=null, notApplicable=false, showNa=false}) => {
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const [showLinesOfBusiness, setShowLinesOfBusiness] = useState(false);
  const handleShowLinesOfBusiness = () => setShowLinesOfBusiness(true);
  const handleCloseLinesOfBusiness = () => setShowLinesOfBusiness(false);

  const [showDocuments, setShowDocuments] = useState(false);
  const handleShowDocuments = () => setShowDocuments(true);
  const handleCloseDocuments = () => setShowDocuments(false);

  const [editingExpires, setEditingExpires] = useState(false);
  const [localExpires, setLocalExpires] = useState(expires);
  const [localEffectiveDate, setLocalEffectiveDate] = useState(effectiveDate);
  const [localPayorNumber, setLocalPayorNumber] = useState(payorNumber);
  const [localActive, setLocalActive] = useState(active);
  const [localNotApplicable, setLocalNotApplicable] = useState(notApplicable);

  // Callback prop
  const [documentCount, setDocumentCount] = useState(0);
  
  const notyf = useContext(NotyfContext);
  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh, tasksRefresh, setTasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    setLocalActive(active);
    setLocalNotApplicable(notApplicable);
    setLocalExpires(expires);
    setLocalEffectiveDate(effectiveDate);
    setLocalPayorNumber(payorNumber);
    setEditingExpires(false);
  }, [practiceId, carrierId, providerId, active, notApplicable, expires, effectiveDate, payorNumber]); // should only need practiceId here (for handling the TIN toggle), but be safe

  function handleClick(portal, e) {
    e.preventDefault();
    window.open(portal, 'newwindow', 'width=800,height=600');
    handleShow();
  }

  const handleEditExpiresClicked = (e) =>
  {
    e.preventDefault();
    setEditingExpires(true);
  }

  const saveExpires = (e) =>
  {
    if (e)
    {      
      e.preventDefault();
    }

    setEditingExpires(false);

    authenticatedFetch({
      path: "provider/participationstatus/" + providerId + "/" + practiceId + "/" + carrierId,
      method: "POST",
      requestData: {active: localActive, notApplicable: localNotApplicable, expiration: localExpires, effectiveDate: localEffectiveDate, payorNumber: localPayorNumber},
      successHandler: function() {
        setTasksRefresh(tasksRefresh + 1); // easiest way to update expires (so undo works the next time) is just to have parent re-render us
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setLocalActive(active);
        setLocalNotApplicable(notApplicable);
        setLocalExpires(expires);
        setLocalEffectiveDate(effectiveDate);
        setLocalPayorNumber(payorNumber);
        alert("Something went wrong; please try again");
      }
    });
  }

  const cancelEditExpires = (e) => {
    e.preventDefault();
    setEditingExpires(false);
    setLocalActive(active);
    setLocalNotApplicable(notApplicable);
    setLocalExpires(expires);
    setLocalEffectiveDate(effectiveDate);
    setLocalPayorNumber(payorNumber);
  }

  const handleActiveChanged = (e) => {
    e.preventDefault();

    if (e.target.value === "INACTIVE")
    {
      // Make it clear that these are going to get deleted
      setLocalExpires(null);
      setLocalEffectiveDate(null);
      setLocalPayorNumber(null);
      setLocalActive(false);
      setLocalNotApplicable(false);
    }
    else if (e.target.value === "ACTIVE")
    {
      setLocalActive(true);
      setLocalNotApplicable(false);
    }
    else if (e.target.value === "NOT_APPLICABLE")
    {
      // Make it clear that these are going to get nulled out
      setLocalExpires(null);
      setLocalEffectiveDate(null);
      setLocalPayorNumber(null);
      setLocalActive(false);
      setLocalNotApplicable(true);
    }
  }

  const handleExpiresChanged = (date, e) => {
    e.preventDefault();
  
    // If they clear it out, date picker sends it back as "", not null
    if (date === "")
    {
      date = null;
    }

    setLocalExpires(date);
  }

  const handleEffectiveDateChanged = (date, e) => {
    e.preventDefault();
  
    // If they clear it out, date picker sends it back as "", not null
    if (date === "")
    {
      date = null;
    }

    setLocalEffectiveDate(date);
  }

  const handlePayorNumberChanged = (name, value) => {
    setLocalPayorNumber(value);
  }

  var activeSelectValue = "INACTIVE";
  if (localActive)
  {
    activeSelectValue = "ACTIVE";
  }
  else if (localNotApplicable)
  {
    activeSelectValue = "NOT_APPLICABLE"; 
  }

  let showCarrier = showNa || !notApplicable;

  // Don't render hidden rows (display:none rows still count toward zebra striping)
  if (!showCarrier) {
    return null;
  }

  return (
    <tr>
        <td style={{maxWidth: '120px', paddingRight: '0'}}>
          {portal == null || portal === '' || !(isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT") || authorities.includes("PROVIDER_PAYOR_PORTAL_VIEW"))
            ?
            <ContactInfoPopover name={carrierName} phone={phone} fax={fax} email={email} />
            :
            <a href="#" onClick={(e) => handleClick(portal, e)}>
              <ExternalLink style={{'height': '12px', 'width': '12px'}}/>&nbsp;<ContactInfoPopover name={carrierName} phone={phone} fax={fax} email={email} />
            </a>
          }
        </td>
        <td>
          {editingExpires?
          <EditableDetail size="sm" originalValue={localPayorNumber} name="payorNumber" placeholder="Number..." onValueChanged={handlePayorNumberChanged}/>
          :
          <span style={{fontFamily: "monospace"}}>{localPayorNumber}</span>
          }
        </td>
        {(isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT") || authorities.includes("PROVIDER_PAYOR_PORTAL_VIEW")) ?
        <>
        <td><Key style={{cursor: 'pointer', height: '16px', width: '16px'}}  onClick={(e) => handleShow()}/></td>
        </>
        : <></>
        }
        <td>
        {editingExpires && (isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT"))
        ?
        <Form.Select size="sm" onChange={(e) => {handleActiveChanged(e)}} value={activeSelectValue} style={{minWidth: "80px"}} >
          <option value="INACTIVE" key="INACTIVE">Inactive</option>
          <option value="ACTIVE" key="ACTIVE">Active</option>
          <option value="NOT_APPLICABLE" key="NOT_APPLICABLE">(n/a)</option>
        </Form.Select>
        :  
        <ParticipationStatusIcon size={18} notApplicable={localNotApplicable} inProgress={progress}  pendingTimestamp={pendingTimestamp} isPending={pending} isActive={localActive} />
        }
        </td>
        <td>
          {localActive && !localNotApplicable ? <Briefcase style={{cursor: 'pointer', height: '16px', width: '16px'}} onClick={(e) => handleShowLinesOfBusiness()}/> : <></>}
        </td>
        <td>
          {localActive && !localNotApplicable ? 
            <>
            {documentCount > 0 ?
            <FileText style={{ color: "#3AAFA9", height: '16px', width: '16px', cursor: "pointer" }} onClick={(e) => {handleShowDocuments()}} />
            :
            <File style={{cursor: 'pointer', height: '16px', width: '16px'}} onClick={(e) => handleShowDocuments()}/>
            }
            <ParticipationDocumentsModal entityType='carrier' providerId={providerId} providerName={providerName} practiceId={practiceId} practiceName={practiceName} entityId={carrierId} entityName={carrierName} show={showDocuments} onClose={handleCloseDocuments} onDocumentCountLoaded={setDocumentCount} />
            </>
          : <></>}
        </td>
        <td>
          <EditableExpires blankText="—" warnWithColors={false} localExpires={localEffectiveDate} localActive={localActive && !localNotApplicable} editingExpires={editingExpires} handleExpiresChanged={handleEffectiveDateChanged} />
        </td>
        <td>
          <EditableExpires blankText="—" localExpires={localExpires} localActive={localActive && !localNotApplicable} editingExpires={editingExpires} handleExpiresChanged={handleExpiresChanged} />
        </td>
        <td>
          {isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT") ?
            <EditableExpiresControls editingExpires={editingExpires} cancelEditExpires={cancelEditExpires} saveExpires={saveExpires} handleEditExpiresClicked={handleEditExpiresClicked} />
          : <>&nbsp;</>
          }
        </td>
        {(isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT") || authorities.includes("PROVIDER_PAYOR_PORTAL_VIEW")) ?
        <LoginCredentialsModal canEdit={isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT")} show={show} onClose={handleClose} entityType="provider" entityId={providerId} entityName={providerName} carrierName={carrierName} carrierId={carrierId} />
        :<></>
        }
        <LinesOfBusinessModal practiceId={practiceId} providerId={providerId} carrierId={carrierId} practiceName={practiceName} providerName={providerName} carrierName={carrierName} show={showLinesOfBusiness} onClose={handleCloseLinesOfBusiness} />
    </tr>
  );
};

const CarrierParticipationStatusTable = ({providerId, providerName, practices}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showNa, setShowNa] = useState(false);
  const [practiceId, setPracticeId] = useState(null);

  const filteredPractices = practices && practices.filter((practice) => !practice.excludedFromCarrierCredentialing);

  const { isH3Admin, authorities, tasksRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  useEffect(() => {
    if (!providerId || !practiceId) {
      return;
    }

    authenticatedFetch({
      path: "provider/participationstatus/" + providerId + "/" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });

  }, [providerId, practiceId, tasksRefresh]);


  function handleTinSelected(practiceId, e) {
    if (e != null) {
      e.preventDefault();
    }

    setPracticeId(practiceId);
  }

  const handleExportClicked = (e) => {
    e.preventDefault();

    setIsExporting(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/participationstatus/provider/" + providerId,
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  var carrierList;

  if (error)
  {
    carrierList = <tr><td colSpan="9">Something went wrong... please reload the page...</td></tr>;
  }
  else if (filteredPractices == null || filteredPractices.length === 0)
  {
    carrierList = <tr><td colSpan="9">(n/a)</td></tr>;
  }
  else if (!isLoaded)
  {
    carrierList = <tr><td colSpan="9">Loading...</td></tr>;
  }
  else
  {
    carrierList = data.carriers && data.carriers.length > 0
      && data.carriers.map((carrier, i) => {
      return (
        <CarrierParticipation key={carrier.id} onSave={handleTinSelected} showNa={showNa} carrierId={carrier.id} practiceId={carrier.practiceId} practiceName={carrier.practiceName} providerId={providerId} providerName={providerName} carrierName={carrier.name} portal={carrier.portalUrl} phone={carrier.phone} email={carrier.email} fax={carrier.fax} active={carrier.isActive} progress={carrier.isInProgress} pending={carrier.isPending} pendingTimestamp={carrier.pendingTimestamp} notApplicable={carrier.isNotApplicable} expires={carrier.expires} effectiveDate={carrier.effectiveDate} payorNumber={carrier.payorNumber} />
      )
    }, this);
  }

  if (!error && isLoaded && (!filteredPractices || filteredPractices.length <= 1)  && (!carrierList || carrierList.length === 0))
  {
    // Single practice with no carriers, hide this completely
    return <></>
  }

  return (
    <Card style={cardOutlineStyle}>
      <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2" style={{paddingBottom: 8}}>
        <Card.Title as="h5" className="mb-0">Participation Status</Card.Title>
        <div className="d-flex flex-wrap align-items-center gap-4">
          <ParticipationStatusToggle handleTinSelected={handleTinSelected} practices={filteredPractices} />
          <Form.Check
            type="switch"
            id="provider-participation-show-na"
            label="Show n/a"
            checked={showNa}
            onChange={() => {setShowNa(!showNa)}}
            className="mb-0"
            style={{fontSize: "0.8rem"}}
          />
          { filteredPractices && filteredPractices.length > 0 &&
            <div className="d-flex align-items-center ps-4" style={{borderLeft: "1px solid #DCE5E3", fontSize: "0.8rem"}}>
              Export{(filteredPractices.length > 1) && " all TINs"}:&nbsp;&nbsp;
              {isExporting ?
              <Spinner animation="border" size="sm" variant="primary" />
              :
              <FontAwesomeIcon onClick={(e) => {handleExportClicked(e)}} icon={faFilePdf} size="lg" style={{color: '#2B7A78', cursor: "pointer"}} title="Export PDF"/>
              }
            </div>
          }
        </div>
      </Card.Header>
      <Card.Body className="pt-0 pb-2">
        <Table striped hover size="sm" className="align-middle mb-0">
          <thead>
            <tr style={{fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "#8a929a"}}>
              <td>Payor</td>
              <td>#</td>
              {(isH3Admin || authorities.includes("PROVIDER_PAYOR_PORTAL_EDIT") || authorities.includes("PROVIDER_PAYOR_PORTAL_VIEW")) ?
              <>
              <td>Portal</td>
              </>
              : <></>
              }
              <td>Active</td>
              <td>LOBs</td>
              <td>Docs.</td>
              <td>Effect. Date</td>
              <td>Expires</td>
              <td>&nbsp;</td>
            </tr>
          </thead>
          <tbody style={{borderTopWidth: 1, borderTopColor: "#DCE5E3"}}>
            {carrierList}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const ProviderCME = ({providerId, providerFormattedName, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNewCme, setAddingNewCme] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const notyf = useContext(NotyfContext);

  useEffect(() => {
    refreshCMEList();
  }, [providerId])

  function refreshCMEList()
  {
    setAddingNewCme(false);
    authenticatedFetch({
      path: "education/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddCMEClicked = (e) => {
    e.preventDefault();
  
    setAddingNewCme(true);
  }

  const onCancelAddNewCme = () => {
    setAddingNewCme(false);
  }

  const handleExportClicked = (e) => {
    e.preventDefault();
    setIsExporting(true);

    // Very non-react way of doing this, but just easier than massively restructuing who owns this state
    const dateRangeOption = document.getElementById('cme-date-range-select').value;

    authenticatedFetch({
      method: "GET",
      path:  "education/export/provider/" + providerId + "?dateRangeOption=" + dateRangeOption,
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  var cmeList = [];
  if (isLoaded)
  {
    cmeList = data.map((cme, i) => {
      return {key: cme.id, id: cme.id, title: cme.title, hours: cme.hours, dollarAmount: cme.dollarAmount, category: cme.category, eventDate: cme.eventDate, documentFilename: cme.documentFilename, updated: cme.updated, updatedBy: cme.updatedBy}
    }, this);
  }

  return (
    <Card style={{overflowY: 'scroll', maxHeight: '700px'}}>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>CMEs
          {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '12px', marginTop: '4px'}}/>
            :
            <Form.Label onClick={(e) => {handleExportClicked(e)}} style={{cursor: "pointer", display: "inline", fontSize: "0.8em", fontWeight: "normal", color: '#2B7A78', marginTop: "7px", marginRight: "3px"}} className="float-end">
              <FontAwesomeIcon icon={faFilePdf} size="lg" style={{color: '#2B7A78'}}/>
              &nbsp;
              Export
            </Form.Label>
          }
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <ProviderCMETable cmeList={cmeList} addingNewCme={addingNewCme} onCancelAddNewCme={onCancelAddNewCme} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshCMEList={refreshCMEList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddCMEClicked(e)}}>+ Add New CME</Button>
          </span>
          : <></>
        }
      </Card.Body>
    </Card>
  )
}

function EventDateFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  return (
    <>
    <Form.Label style={{display: "inline", fontSize: "0.9em"}}>Show CMEs completed:
      &nbsp;
      <Form.Select
        id="cme-date-range-select"
        size="sm"
        className="mt-2"
        style={{display: "inline", width: "auto"}}
        value={filterValue || ""}
        onChange={(e) => {
          setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
        >
          <option value="">(all-time)</option>
          <option value="calendarYear">since start of calendar year</option>
          <option value="yearAgo">in past 12 months</option>
          <option value="twoYearsAgo">in past 24 months</option>
        </Form.Select>
      </Form.Label> 
      </>
  );
}

function customEventDateFilter(rows, columnIds, filterValue) {
  return rows.filter((row) => {
    if (filterValue)
    {
      if (!row.original.eventDate || row.original.eventDate === "")
      {
        // Always show if no date (originally were hiding, but confusing)
        return true;
      }

      const components = row.original.eventDate.split("-");
      if (components.length === 3)
      {
        const expirationDate = new Date(components[0], components[1] - 1, components[2]);
        const now = new Date();
        now.setHours(0);
        now.setMinutes(0);
        now.setSeconds(0);
        now.setMilliseconds(0);
        if (filterValue === "calendarYear")
        {
          return expirationDate.getFullYear() === now.getFullYear();
        }
        else if (filterValue === "yearAgo")
        {
          const yearAgo = new Date(new Date().setFullYear(now.getFullYear() - 1));
          return expirationDate >= yearAgo;
        }
        else if (filterValue === "twoYearsAgo")
        {
          const twoYearsAgo = new Date(new Date().setFullYear(now.getFullYear() - 2));
          return expirationDate >= twoYearsAgo;
        }
      }
      
      return false;
    }

    return true;
  });
}

function CmeDefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <Form.Control 
      size="sm"
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} CME(s)...`}
      className="mt-2"
    />
  );
}

// TODO move this somewhere reusable
function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  const options = React.useMemo(() => {
    const options = new Set();
    rawCMECategories.forEach((rawCategory) => {
      if (rawCategory.value !== "")
      {
        options.add(rawCategory);
      }
    });
    return [...options.values()];
  }, []);

  // Render a multi-select box
  return (
    <Form.Select size="sm" style={{width: 'auto'}}
      value={filterValue === "" ? "" : filterValue || "all"}
      onChange={(e) => {
        if (e.target.value === "all")
        {
          // This will just short circuit and show all - our custom filter function will NOT be called
          setFilter(undefined);
        }
        else
        {
          setFilter(e.target.value);
        }
      }}
      className="mt-2"
    >
      <option value="all">All</option>
      {options.map((option, i) => (
        <option key={i} value={option.value}>
          {option.label}
        </option>
      ))}
      <option value="">(no category)</option>
    </Form.Select>
  );
}

// TODO move somewhere resuable
function customCategoryFilter(rows, columnIds, filterValue) {
  return rows.filter((row) => {
    if (filterValue === "" && 
    (row.original.category == null || row.original.category === ""))
    {
      // Comes from server as null right now if not set, but handle blank string to be safe
      return true;
    }

    return filterValue === row.original.category;
  });
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});


const CME = ({ id, providerId, title, hours, dollarAmount, category, eventDate, documentFilename, refreshCMEList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localHours, setLocalHours] = useState(hours);
  const [localDollarAmount, setLocalDollarAmount] = useState(dollarAmount)
  const [localCategory, setLocalCategory] = useState(category);
  const [localEventDate, setLocalEventDate] = useState(eventDate);
  
  const [newDocumentName, setNewDocumentName] = useState(null);
  const [newDocumentContentType, setNewDocumentContentType] = useState(null);
  const [newDocumentFile, setNewDocumentFile] = useState(null);

  const notyf = useContext(NotyfContext);

  function handleDocumentClick(e) {
    e.preventDefault();

    if (isSaving || !documentFilename)
    {
      return;
    }

    authenticatedFetch({
      method: "POST",
      requestData: {filename: documentFilename},
      path:  "education/" + id + "/file/signeddownloadurl",
      successHandler: function(result) {
        window.open(result.url);
      },
      errorHandler: function(error) {
        alert("Something went wrong; please try again.")
      }
    });
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete: " + title + "?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "education/" + id,
        successHandler: function(result) {
          refreshCMEList();
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalTitle(title);
    setLocalHours(hours);
    setLocalDollarAmount(dollarAmount);
    setLocalCategory(category);
    setLocalEventDate(eventDate);

    setNewDocumentName(null);
    setNewDocumentContentType(null);
    setNewDocumentFile(null);

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  const uploadDocument = (cmeId) => {
    if (newDocumentFile !== null && newDocumentName !== null && newDocumentContentType !== null)
    {
      authenticatedFetch({
        method: "POST",
        requestData: {filename: newDocumentName, contentType: newDocumentContentType},
        path: "education/" + cmeId + "/file/signeduploadurl",
        successHandler: function(result) {
          // Got a signed S3 url, PUT to it
          var requestMetadata = {
            method: 'PUT',
            headers: {
                'Content-Type': newDocumentContentType
            },
            body: newDocumentFile
          };
          
          fetch(result.url, requestMetadata).then(res => {
            if (res.ok)
            {
              setNewDocumentName(null);
              setNewDocumentContentType(null);
              setNewDocumentFile(null);
        
              setIsSaving(false); 
              refreshCMEList();
              notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
            }
            else
            {
              setIsSaving(false);
              setEditing(true);
              notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
            }
          }).catch((error) => {
            setIsSaving(false);
            setEditing(true);
            notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
          });
        },
        errorHandler: function(error) {
          setIsSaving(false);
          setEditing(true);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
    else
    {
      // Make sure all three are nulled out
      setNewDocumentName(null);
      setNewDocumentContentType(null);
      setNewDocumentFile(null);

      setIsSaving(false);
      refreshCMEList();
      notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
    }
  }

  function saveEdit(e) {
    e.preventDefault();

    if (localTitle === '')
    {
      alert("Title can't be blank!");
      return;
    }

    setEditing(false);
    setIsSaving(true);

    if (title !== localTitle || hours !== localHours || dollarAmount !== localDollarAmount || category !== localCategory || eventDate !== localEventDate)
    {
      authenticatedFetch({
        method: id ? "PUT" : "POST",
        requestData: {providerId: parseInt(providerId), title: localTitle, category: localCategory, eventDate: localEventDate, hours: parseFloat(localHours), dollarAmount: parseFloat(localDollarAmount)},
        path: id ? "education/" + id  : "education",
        successHandler: function(result) {
          // Do the save first; if it's a new CME, we need the server ID before we can do the document upload
          uploadDocument(result.id);
        },
        errorHandler: function() {
          setIsSaving(false);
          setEditing(true);
          setLocalTitle(title);
          setLocalHours(hours);
          setLocalDollarAmount(dollarAmount);
          setLocalCategory(category);
          setLocalEventDate(eventDate);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
    else if (id)
    {
      uploadDocument(id);
    }
  }

  const handleEventDateChanged = (date, e) => {
    e.preventDefault();
  
    // If they clear it out, date picker sends it back as "", not null
    if (date === "")
    {
      date = null;
    }

    setLocalEventDate(date);
  }

  function handleTitleChanged(e) {
    e.preventDefault();
    setLocalTitle(e.target.value);
  }

  function handleHoursChanged(e) {
    e.preventDefault();
    setLocalHours(e.target.value);
  }

  function handleDollarAmountChanged(e) {
    e.preventDefault();
    setLocalDollarAmount(e.target.value);
  }

  function handleCategoryChanged(e) {
    e.preventDefault();
    setLocalCategory(e.target.value);
  }

  const handleDocumentAdded = (e) => {
    if (e.target.files && e.target.files.length === 1)
    {
      setNewDocumentName(e.target.files[0].name);
      setNewDocumentContentType(e.target.files[0].type);
      setNewDocumentFile(e.target.files[0]);
    }
  }
  
  var selectedCategoryLabel = '';
  if (localCategory !== '')
  {
    for (let i = 0; i < rawCMECategories.length; i++)
    {
      if (localCategory === rawCMECategories[i].value)
      {
        selectedCategoryLabel = rawCMECategories[i].label;
        break;
      }
    }
  }

  let categoryList = rawCMECategories.map((category, i) => {
    return (
      <option key={i} value={category.value}>{category.label}</option>
    )
  }, this);

  var titleDisplay = localTitle
  if (titleDisplay && titleDisplay.length > 100)
  {
    titleDisplay = titleDisplay.substring(0, 98) + "...";
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Control size="sm" style={{width: "220px"}} autoFocus={true} maxLength={512} value={localTitle} onChange={(e) => {handleTitleChanged(e)}} />
    </td>
    :
    <td style={{maxWidth: "250px"}}>
      <OverlayTrigger
      triggers="[hover,focus]"
      placement="bottom"
      overlay={<Tooltip >{localTitle}</Tooltip>}
      >
        <span>{titleDisplay}</span>
      </OverlayTrigger>
    </td>
    }
    <td style={{fontFamily: "monospace"}}>
      {editing?
      <Form.Control size="sm" style={{width: "55px"}} type="number" maxLength={10} value={localHours} onChange={(e) => {handleHoursChanged(e)}} />
      : localHours
      }
    </td>
    <td style={{fontFamily: "monospace"}}>
      {editing?
      <Form.Control size="sm" style={{width: "55px"}} type="number" maxLength={10} value={localDollarAmount} onChange={(e) => {handleDollarAmountChanged(e)}} />
      : localDollarAmount ? currencyFormatter.format(localDollarAmount) : ""
      }
    </td>
    <td>
      {editing?
        <Form.Select size="sm" value={localCategory} onChange={(e) => {handleCategoryChanged(e)}} style={{width: "auto"}} >
          {categoryList}
        </Form.Select>
      :
      <Badge pill bg="secondary">{selectedCategoryLabel}</Badge>
      }
    </td>
    <td><EditableExpires warnWithColors={false} localExpires={localEventDate} localActive={true} editingExpires={editing} handleExpiresChanged={handleEventDateChanged} blankText="" /></td>
    {editing ?
    <td>
      {newDocumentName ? (
        <FileText style={{ color: "#3AAFA9", height: 16, width: 16 }} />
      ) : (
        <label className="form-control-sm" style={{ cursor: "pointer" }}>
          ...
          <Form.Control
            type="file"
            size="sm"
            hidden
            onChange={handleDocumentAdded}
          />
        </label>
      )}
    </td>
    :
    <td style={{cursor: isSaving || !documentFilename ? "" : "pointer"}}  onClick={(e) => handleDocumentClick(e)}>
      { documentFilename && !isSaving && <Download style={{'color': '#3AAFA9', 'height': '16px', 'width': '16px'}}/>}
      { isSaving && <Spinner animation="border" size="sm" />}
    </td>
    }
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const ProviderCMETable = ({providerId, addingNewCme, onCancelAddNewCme, cmeList, isLoaded, error, canEdit, refreshCMEList}) => {
  const data = cmeList;

  const columns = React.useMemo(
    () => [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Title",
        accessor: "title",
        key: "title",
        Filter: CmeDefaultColumnFilter,
      },
      {
        Header: "Hours",
        accessor: "hours",
        key: "hours",
      },
      {
        Header: "Cost",
        accessor: "dollarAmount",
        key: "dollarAmount",
      },
      {
        Header: "Category",
        accessor: "category",
        key: "category",
        Filter: SelectColumnFilter,
        filter: customCategoryFilter,
      },
      {
        Header: "Date",
        accessor: "eventDate",
        key: "eventDate",
        Filter: EventDateFilter,
        filter: customEventDateFilter,
        sortType: (rowA, rowB, columnId, desc) => {
          if (rowA.values[columnId] !== rowB.values[columnId]) 
          {
            if (!rowA.values[columnId] || rowA.values[columnId] === "")
            {
              return 1;
            }
            if (!rowB.values[columnId] || rowB.values[columnId] === "")
            {
              return -1;
            }
  
            return rowA.values[columnId].localeCompare(rowB.values[columnId]);
          }
  
          return rowB.original.title.localeCompare(rowA.original.title);;
        },
      },
      {
        Header: "File",
        accessor: "documentFilename",
        key: "documentFilename",
      },
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    ],
  []
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
            {
                id: 'eventDate',
                desc: true
            },
            {
              id: 'title',
              desc: false
          }
        ],
        filters: [
          {
              id: 'eventDate',
              value: "twoYearsAgo"
          },
        ], 
      }
    },
    useFilters,
    useSortBy,
  );

  var totalHours = 0.0;
  var totalCost = 0.0;
  rows.map((row) => {
    if (row.values.hours)
      totalHours += row.values.hours;

    if (row.values.dollarAmount)
      totalCost += row.values.dollarAmount;

      return row;
  });

    // Extract the event date filter
    var eventDateFilter = "";
    for (var i=0; i < headerGroups.length; i++)
    {
      for (var j=0; j < headerGroups[i].headers.length; j++)
      {
        var column = headerGroups[i].headers[j];
        if (column.id === "eventDate")
        {
          eventDateFilter = column.render("Filter");
          break;
        }
      }
    }

  return (
    <>
    <Table className="table-sm" striped bordered hover {...getTableProps()}>
    <thead>
      {headerGroups.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => {
            const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "documentFilename" || column.id === "category" || column.id === "title") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
            return (
              <th {...restHeaderProps} key={key}>
                {column.render("Header")}
                {(column.id !== "icons" && column.id !== "documentFilename" && column.id !== "category" && column.id !== "title") ? (
                  <>
                  {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                      ) : (
                        <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                      )
                    ) : (
                      <FontAwesomeIcon icon={faSort} className="ms-2" />
                  )}
                  </>
                ) : 
                  (
                    (column.id === "category" || column.id === "title") ? <>{column.render("Filter")}</>
                    :
                    <></>
                  )
                }
              </th>
            )
          }
          )}
        </tr>
      ))}
    </thead>
    <tbody {...getTableBodyProps()}>
      {
      error ? (<tr><td colSpan={7}>Something went wrong; please reload the page...</td></tr>) : ( 
        !isLoaded ? (<tr><td colSpan={7}>Loading...</td></tr>) : (
          rows.map((row) => {
          prepareRow(row);
          const { key, ...restRowProps } = row.getRowProps();
          return (
            <tr {...restRowProps} key={key}>
              <CME providerId={providerId} id={row.values.id} key={row.values.id} title={row.values.title ? row.values.title : ""} hours={row.values.hours ? row.values.hours : ""} dollarAmount={row.values.dollarAmount ? row.values.dollarAmount : ""} category={row.values.category ? row.values.category : ""} eventDate={row.values.eventDate} documentFilename={row.values.documentFilename} refreshCMEList={refreshCMEList} canEdit={canEdit}/>
            </tr>
          );
        })))
      }
      {
      addingNewCme ?
        <tr><CME providerId={providerId} key={0} startInEditMode={true} title={""} hours={""} dollarAmount={""} category={""} onCancelEdit={onCancelAddNewCme} canEdit={canEdit} refreshCMEList={refreshCMEList}/></tr>
      : <></>
      }
      <tr>
        <td><b>Total</b></td>
        <td><b>{Number((totalHours).toFixed(2))}</b></td>
        <td><b>{currencyFormatter.format(totalCost)}</b></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    </tbody>
  </Table>
  {eventDateFilter}
  </>
  )
}

const SupervisingProviderSelect = ({ handleSupervisingProvidersChanged, selectedSupervisingProviders, providerId, forceRefresh}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
  
    useEffect(() => {
        authenticatedFetch({
            path: "provider/revenueteam/supervising/provider/" + providerId,
                successHandler: function(result) {
                setIsLoaded(true);
                setData(result);
            },
            errorHandler: function(error) {
                setIsLoaded(true);
                setError(error);
            }
        });
    }, [])
  
    const options = data.map((provider) => {
        return {value: provider.id, label: provider.name};
    });

    if (!isLoaded)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
      return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
        return (
            <div style={{width: "100%"}}>
                <Select
                options={options}
                onChange={(selected) => {
                    handleSupervisingProvidersChanged(selected);
                }}
                value={selectedSupervisingProviders}
                placeholder="Add a provider..."
                isMulti={true}
                isClearable={true}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                  ...theme.colors,
                    primary50: '#DEF2F1',
                    primary25: '#DEF2F1',
                    primary: '#3AAFA9',
                  },
                })}
                />
            </div>
        )
    }
}

// One clickable row in the Exports / Form Generation lists: regular-weight label, small teal
// file icon, and a same-size spinner while the export runs
const ExportRow = ({ label, format = "PDF", busy, onClick }) => (
  <tr style={{cursor: busy ? "default" : "pointer"}} onClick={busy ? undefined : onClick}>
    <td style={{fontSize: "13px"}}>{label}</td>
    <td className="text-end" style={{width: "1%", whiteSpace: "nowrap"}}>
      {busy ?
      <Spinner animation="border" size="sm" variant="primary" />
      :
      <FontAwesomeIcon icon={format === "CSV" ? faFileCsv : faFilePdf} title={"Download " + format} style={{color: "#2B7A78"}} />
      }
    </td>
  </tr>
);

const CarrierEnrollmentForm = ({formId, formName, format, message}) => {
  const[isExporting, setIsExporting] = useState(false);

  let { id } = useParams();

  const notyf = useContext(NotyfContext);

  const handleExportClicked = (e) => {
    e.preventDefault();

    if (message && message.length > 0)
    {
      alert(message);
    }

    setIsExporting(true);

    authenticatedFetch({
      method: "GET",
      path:  "provider/carrierenrollmentform/" + id + "/" + formId,
      successHandler: function(result) {
        for (var i=0; i<result.urls.length; i++)
        {
          window.open(result.urls[i]);
        }
        
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  return <ExportRow label={formName} format={format} busy={isExporting} onClick={handleExportClicked} />;
}

const CarrierEnrollmentForms = () => {
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  let { id } = useParams();

  useEffect(() => {
      authenticatedFetch({
          path: "provider/carrierenrollmentforms/" + id,
              successHandler: function(result) {
              setIsLoaded(true);
              setData(result);
          },
          errorHandler: function(error) {
              setIsLoaded(true);
              setError(error);
          }
      });
  }, [])

  var forms;
  if (!isLoaded)
  {
    forms = <tr><td colSpan={2}>Loading...</td></tr>;
  }
  else if (error || !data)
  {
    forms = <tr><td colSpan={2}>Error... Please reload the page...</td></tr>;
  }
  else
  {
    if (!data || data.length === 0)
    {
      forms = <tr><td colSpan={2}>No forms configured...</td></tr>;
    }
    else
    {
      forms = data.map((form, i) => {
        return (
          <CarrierEnrollmentForm key={form.id} formId={form.id} formName={form.name} format={form.format} message={form.message}/>
        )
      }, this);
    }
  }

  return (
    <Card style={cardOutlineStyle}>
      <DetailCardHeader title="Form Generation" expanded={expanded} onToggle={() => setExpanded(!expanded)} />
      <Collapse in={expanded}>
        <div className="px-4 pb-3">
          <Table className="table-sm align-middle mb-0" striped hover>
            <tbody>
              {forms}
            </tbody>
          </Table>
        </div>
      </Collapse>
  </Card>
  ); 
}

const PracticeInfoModal = ({ show, onClose, practice}) => {

  const [mostRecentlyCopiedValue, setMostRecentlyCopiedValue] = useState(null);
  const handleCopyValue = (value) => {
    setMostRecentlyCopiedValue(value);
  }

  var copyAddressFormatted = "";
  copyAddressFormatted += (practice.address1 ? practice.address1 : "")
  copyAddressFormatted += (practice.address2 ? ("\n" + practice.address2) : "")
  copyAddressFormatted += "\n" + (practice.city ? practice.city : "") + (practice.state ? (", " + practice.state) : "") + (practice.zip ? (" " + practice.zip) : "");

  var copyMailingAddressFormatted = "";
  copyMailingAddressFormatted += (practice.mailingAddress1 ? practice.mailingAddress1 : "")
  copyMailingAddressFormatted += (practice.mailingAddress2 ? ("\n" + practice.mailingAddress2) : "")
  copyMailingAddressFormatted += "\n" + (practice.mailingCity ? practice.mailingCity : "") + (practice.mailingState ? (", " + practice.mailingState) : "") + (practice.mailingZip ? (" " + practice.mailingZip) : "");

  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Practice Details for: <b>{practice.name}</b></span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table className="table-borderless"> 
            <tbody>
              <tr>
                <td><b>TIN:</b></td>
                <td>{practice.tin}</td>
                <td>{practice.tin && <CopyToClipboardToggle name={practice.tin} text={practice.tin} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
              <tr>
                <td><b>NPI:</b></td>
                <td>{practice.npi}</td>
                <td>{practice.npi && <CopyToClipboardToggle name={practice.npi} text={practice.npi} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
              <tr>
                <td>
                  <b>Address:</b>
                </td>
                <td>
                {practice.address1}
                <br />
                {practice.address2
                ?
                <>{practice.address2}
                <br />
                </>
                : <></>
                }
                {practice.city}{practice.city != null ?", " : ""}{practice.state} {practice.zip}
                </td>
                <td>
                {practice.address1 && <CopyToClipboardToggle name={copyAddressFormatted} text={copyAddressFormatted} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}
                </td>
              </tr>
              <tr>
                <td>
                  <b>Mailing Address:</b>
                </td>
                <td>
                {practice.mailingAddress1}
                <br />
                {practice.mailingAddress2
                ?
                <>{practice.mailingAddress2}
                <br />
                </>
                : <></>
                }
                {practice.mailingCity}{practice.mailingCity != null ?", " : ""}{practice.mailingState} {practice.mailingZip}
                </td>
                <td>
                {practice.mailingAddress1 && <CopyToClipboardToggle name={copyMailingAddressFormatted} text={copyMailingAddressFormatted} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}
                </td>
              </tr>
              <tr>
                <td><b>Phone:</b></td>
                <td>{practice.phone1}</td>
                <td>{practice.phone1 && <CopyToClipboardToggle name={practice.phone1} text={practice.phone1} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
              <tr>
                <td><b>Fax:</b></td>
                <td>{practice.fax}</td>
                <td>{practice.fax && <CopyToClipboardToggle name={practice.fax} text={practice.fax} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
              <tr>
                <td><b>Email:</b></td>
                <td>{practice.email1}</td>
                <td>{practice.email1 && <CopyToClipboardToggle name={practice.email1} text={practice.email1} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
              <tr>
                <td><b>Group Medicare:</b></td>
                <td>{practice.groupMedicare}</td>
                <td>{practice.groupMedicare && <CopyToClipboardToggle name={practice.groupMedicare} text={practice.groupMedicare} mostRecentlyCopied={mostRecentlyCopiedValue} onCopyValue={handleCopyValue} />}</td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
          <Button onClick={onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
  );
}

const BlockedList = ({providerId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const { tasksRefresh } = useContext(UserSettingsContext);
  const [daysDesc, setDaysDesc] = useState(true);

  const refreshData = () => {
    if (!providerId) return;

    authenticatedFetch({
      path: "task/blockedinternally/v2/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  };

  useEffect(() => {
    refreshData();
  }, [providerId, tasksRefresh])


  if (error || !data)
  {
    return <TaskCard className="flex-fill w-100" title="Waiting" count={0}><div style={{ padding: "12px 16px" }}>Something went wrong... please reload the page...</div></TaskCard>
  }

  if (!isLoaded || data.length === 0)
  {
    return null;
  }

  return (
    <TaskCard
      collapsible
      className="flex-fill w-100"
      title="Waiting"
      count={data.length}
      headerRight={<SortButton label="Days" ascending={!daysDesc} onToggle={() => setDaysDesc(!daysDesc)} ascTitle="Fewest days first" descTitle="Most days first" />}
    >
      {[...data].sort((a, b) => daysDesc ? b.days - a.days : a.days - b.days).map((task) => {
        const days = task.days;
        const daysColor = days > 2 ? "#C2453D" : "#6B7274";
        return (
          <TaskSummaryRow
            key={task.id}
            task={task}
            entityType="provider"
            metric={<span style={{ fontSize: "12.5px", color: daysColor, fontWeight: days > 2 ? 500 : 400 }}>{days} day{days === 1 ? "" : "s"}</span>}
          />
        );
      })}
    </TaskCard>
  )
}

const Provider = () => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);  // TODO these should really be separate components
  const [editingDemographics, setEditingDemographics] = useState(false);
  const [localData, setLocalData] = useState([])
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDemographicsExpanded, setIsDemographicsExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [newProfilePhotoName, setNewProfilePhotoName] = useState(null);
  const [newProfilePhotoContentType, setNewProfilePhotoContentType] = useState(null);
  const [newProfilePhotoFile, setNewProfilePhotoFile] = useState(null);

  const [forceSpecialtyRefresh, setForceSpecialtyRefresh] = useState(0);
  const [forceSupervisingProviderRefresh, setForceSupervisingProviderRefresh] = useState(0);

  const [isExportingDemographicDetails, setIsExportingDemographicDetails] = useState(false);
  const [isExportingProfile, setIsExportingProfile] = useState(false);
  const [isExportingCv, setIsExportingCv] = useState(false);
  const [isExportingLocations, setIsExportingLocations] = useState(false);
  const [isExportsExpanded, setIsExportsExpanded] = useState(true);

  const [infoPractice, setInfoPractice] = useState(null);

  let { id } = useParams();

  const notyf = useContext(NotyfContext);
  const { isH3Admin, authorities, navbarRefresh, setNavbarRefresh, documentsRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    authenticatedFetch({
      path: "provider/" + id,
      successHandler: function(result) {
        setIsLoaded(true);
        result = withGroupedSupervisors(result);
        setData(result);
        // Otherwise seem to point to the same obj, and updating one updates the other?
        var copy={};
        Object.assign(copy, result);
        setLocalData(copy);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [id, documentsRefresh]) // State license, DEA, etc. updates that are now read-only here

  const handleEditClicked = () => {
    // Treat it like a cancel of the other one if it was open, for simplicity
    setEditingDetails(false);
    setEditingDemographics(false);  
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditing(true);
  }

  const handleEditDetailsClicked = () => {
    setEditing(false);
    setEditingDemographics(false);
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditingDetails(true);
  }

  const handleEditDemographicsClicked = () => {
    setEditing(false);
    setEditingDetails(false);
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditingDemographics(true);
  }

  const handleExpandClicked = (e) => {
    e.preventDefault();
    setIsExpanded(true);
  }

  const handleCollapseClicked = (e) => {
    e.preventDefault();
    setIsExpanded(false);
  }

  const handleProfilePhotoAdded = (e) => {
    if (e.target.files && e.target.files.length === 1)
    {
      setNewProfilePhotoName(e.target.files[0].name);
      setNewProfilePhotoContentType(e.target.files[0].type);
      setNewProfilePhotoFile(e.target.files[0]);
    }
  }

  const handleExportDemographicDetailsClicked = (e) => {
    e.preventDefault();
    setIsExportingDemographicDetails(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/providerdemographicdetails/" + id,
      successHandler: function(result) {
        window.open(result.url);
        setIsExportingDemographicDetails(false);
      },
      errorHandler: function(error) {
        setIsExportingDemographicDetails(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleExportProfileClicked = (e) => {
    e.preventDefault();
    setIsExportingProfile(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/providerprofile/" + id,
      successHandler: function(result) {
        window.open(result.url);
        setIsExportingProfile(false);
      },
      errorHandler: function(error) {
        setIsExportingProfile(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }
  
  const handleExportCvClicked = (e) => {
    e.preventDefault();
    setIsExportingCv(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/providercv/" + id,
      successHandler: function(result) {
        window.open(result.url);
        setIsExportingCv(false);
      },
      errorHandler: function(error) {
        setIsExportingCv(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleExportLocationsClicked = (e) => {
    e.preventDefault();
    setIsExportingLocations(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/providerlocations/" + id,
      successHandler: function(result) {
        window.open(result.url);
        setIsExportingLocations(false);
      },
      errorHandler: function(error) {
        setIsExportingLocations(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const updateProviderInfo = (editDetails, editDemographics) => {
    const dataToSend = {}
    Object.assign(dataToSend, localData);
    dataToSend['profilePhotoFilename'] = newProfilePhotoName;
    // The server wants one {id, state} entry per supervisor/state pair
    dataToSend['supervisingProviders'] = flattenSupervisingProviders(localData.supervisingProviders);

    authenticatedFetch({
      path: "provider/" + id,
      method: "POST",
      requestData: dataToSend,
      successHandler: function(result) {
        setIsSaving(false);
        setNewProfilePhotoName(null);
        setNewProfilePhotoContentType(null);
        setNewProfilePhotoFile(null);
        result = withGroupedSupervisors(result);
        setData(result);
        var copy={};
        Object.assign(copy, result);
        setLocalData(copy);

        notyf.open({type: "success", message: "Provider information updated!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});

        setNavbarRefresh(navbarRefresh + 1);
      },
      errorHandler: function() {
        setIsSaving(false);
        if (editDetails)
        {
          setEditingDetails(true);
        }
        else if (editDemographics)
        {
          setEditingDemographics(true);
        }
        else
        {
          setEditing(true);
        }
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  // Just shared this function for now, since the data is shared
  const handleSaveClicked = (editDetails, editDemographics) => {
    setEditing(false);
    setEditingDemographics(false);
    setEditingDetails(false);
    setIsSaving(true);

    // Do the photo upload first, so we can then record the new filename
    if (newProfilePhotoFile != null && newProfilePhotoName != null && newProfilePhotoContentType != null)
    {
      authenticatedFetch({
        method: "POST",
        requestData: {filename: newProfilePhotoName, contentType: newProfilePhotoContentType, isPublic: true},
        path: "provider/" + id + "/file/signeduploadurl",
        successHandler: function(result) {
          // Got a signed S3 url, PUT to it
          var requestMetadata = {
            method: 'PUT',
            headers: {
                'Content-Type': newProfilePhotoContentType
            },
            body: newProfilePhotoFile
          };
          
          fetch(result.url, requestMetadata).then(res => {
            if (res.ok)
            { 
              updateProviderInfo(editDetails, editDemographics);
            }
            else
            {
              setIsSaving(false);
              setEditing(true);
              notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
            }
          }).catch((error) => {
            setIsSaving(false);
            setEditing(true);
            notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
          });
        },
        errorHandler: function(error) {
          setIsSaving(false);
          setEditing(true);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
    else
    {
      // Make sure all three are nulled out
      setNewProfilePhotoName(null);
      setNewProfilePhotoContentType(null);
      setNewProfilePhotoFile(null);
      updateProviderInfo(editDetails, editDemographics); 
    }
  }

  const handleCancelClicked = () => {
    setEditing(false);
    setEditingDemographics(false);
    setEditingDetails(false);

    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setNewProfilePhotoName(null);
    setNewProfilePhotoContentType(null);
    setNewProfilePhotoFile(null);
  }

  const handleSpecialtiesChanged = (selectedSpecialties)  => {
    var specialties = [];
    for(var i=0; i<selectedSpecialties.length; i++)
    {
      var index = selectedSpecialties[i].label.indexOf(":");
      var code = selectedSpecialties[i].label.substring(0, index);
      var name = selectedSpecialties[i].label.substring(index+2);

      specialties.push({id: selectedSpecialties[i].value, code: code, name: name});
    }

    localData["specialties"] = specialties
    setLocalData(localData)

    // TODO - doesn't trigger the refresh with the specialty subobject update, so using this hack for now
    setForceSpecialtyRefresh(forceSpecialtyRefresh + 1);
  }

  const handleSupervisingProvidersChanged = (selected) => {
      // Comes as an array (possibly null when cleared) when multi
      // Keep the state of supervisors that were already selected
      const existing = localData.supervisingProviders || [];
      localData["supervisingProviders"] = (selected || []).map((option) => {
        const previous = existing.find((provider) => provider.id === option.value);
        return {id: option.value, name: option.label, states: previous ? previous.states || [] : []};
      });
      setLocalData(localData);

      setForceSupervisingProviderRefresh(forceSupervisingProviderRefresh + 1);
  }

  const handleSupervisingProviderStatesChanged = (providerId, states) => {
      localData["supervisingProviders"] = (localData.supervisingProviders || []).map((provider) =>
        provider.id === providerId ? {...provider, states: states || []} : provider);
      setLocalData(localData);

      setForceSupervisingProviderRefresh(forceSupervisingProviderRefresh + 1);
  }

  const onValueChanged = (name, newValue) => {
    if (newValue != null && name != null && (name.indexOf("zip") > -1 || name.indexOf("phone") > -1 || name.indexOf("pager") > -1 || name.indexOf("npi") > -1 || name.indexOf("ssn") > -1 ))
    {
      // Remove any remaining mask characters from the truly numeric one; can't do anything about the phone parens and dash on partially completed phones
      newValue = newValue.replace(/_/g, '');

      if (name.indexOf("zip") > -1 && newValue.length <= 6)
      {
        // Chop the trailling dash
        newValue = newValue.substring(0, newValue.length - 1); // Comes through as 980- for example
      }
    }

    if(name === "dob" && newValue === "")
    {
      newValue = null;
    }

    localData[name] = newValue
    setLocalData(localData)
  }

  if (!isLoaded)
  {
    return <div>Loading...</div>
  }
  else if (error || !data)
  {
    return <div>Something went wrong... please reload the page...</div>
  }
  else
  {
    var priorAddressesReadOnly = '';
    if (localData.priorAddresses && localData.priorAddresses.length > 0) {
      priorAddressesReadOnly = localData.priorAddresses.split("\n").map(function(item, idx) {
        return ( 
            <span key={idx}>
                {item}
                <br />
            </span>
         )
      });
    }

    var priorNamesReadOnly = '';
    if (localData.priorNames && localData.priorNames.length > 0) {
      priorNamesReadOnly = localData.priorNames.split("\n").map(function(item, idx) {
        return ( 
            <span key={idx}>
                {item}
                <br />
            </span>
         )
      });
    }

    var maritalStatusReadOnly = '';
    if (localData.maritalStatus !== '')
    {
      for (let i = 0; i < rawMaritalStatuses.length; i++)
      {
        if (localData.maritalStatus === rawMaritalStatuses[i].value)
        {
          maritalStatusReadOnly = rawMaritalStatuses[i].label;
          break;
        }
      }
    }

    var dobDisplay = "";
    var editableDobValue = ""

    if (localData.dob != null)
    {
      var parts = localData.dob.split("-");
      const dobDate = new Date(parts[0], parts[1] - 1, parts[2]);  // Avoid the UTC->local timezone translation issue with js
      dobDisplay = new Date(dobDate).toLocaleDateString("en-US");

      editableDobValue = dobDate.getFullYear() + "-" + String(dobDate.getMonth() + 1).padStart(2, '0') + "-" + String(dobDate.getDate()).padStart(2, '0');
    }

    var selectedSpecialtiesTransformed = [];
    if(localData.specialties)
    {
      for(var i = 0; i < localData.specialties.length; i++)
      {
        selectedSpecialtiesTransformed.push({value: localData.specialties[i].id, label: localData.specialties[i].code + ": " + localData.specialties[i].name});
      }
    }

    var selectedSupervisingProvidersTransformed = (localData.supervisingProviders || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((provider) => ({value: provider.id, label: provider.name}));

    var sexReadOnly = '';
    if (localData.sex !== '')
    {
      for (let i = 0; i < rawSexTypes.length; i++)
      {
        if (localData.sex === rawSexTypes[i].value)
        {
          sexReadOnly = rawSexTypes[i].label;
          break;
        }
      }
    }

    let canRunDemographicDetailsReport = (isH3Admin || (
      (authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT")) &&
      (authorities.includes("PROVIDER_SENSITIVE_DETAILS_VIEW") || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")) &&
      (authorities.includes("PROVIDER_DEMOGRAPHICS_VIEW") || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")) &&
      (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")) &&
      (authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")) &&
      (authorities.includes("PROVIDER_PEER_REFERENCE_VIEW") || authorities.includes("PROVIDER_PEER_REFERENCE_EDIT")) &&
      (authorities.includes("PROVIDER_TRAINING_VIEW") || authorities.includes("PROVIDER_TRAINING_EDIT")) &&
      (authorities.includes("PROVIDER_WORK_HISTORY_VIEW") || authorities.includes("PROVIDER_WORK_HISTORY_EDIT")) &&
      (authorities.includes("PROVIDER_MALPRACTICE_INSURANCE_VIEW") || authorities.includes("PROVIDER_MALPRACTICE_INSURANCE_EDIT")) &&
      (authorities.includes("PROVIDER_BOARD_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT"))
    ));

    let canRunProfileReport = (isH3Admin || (
      (authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT")) &&
      (authorities.includes("PROVIDER_DEMOGRAPHICS_VIEW") || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")) &&
      (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")) &&
      (authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")) &&
      (authorities.includes("PROVIDER_PEER_REFERENCE_VIEW") || authorities.includes("PROVIDER_PEER_REFERENCE_EDIT")) &&
      (authorities.includes("PROVIDER_BOARD_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT")) &&
      (authorities.includes("PROVIDER_CME_VIEW") || authorities.includes("PROVIDER_CME_EDIT"))
    ));

    let canRunCvReport= (isH3Admin || (
      (authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT")) &&
      (authorities.includes("PROVIDER_DEMOGRAPHICS_VIEW") || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")) &&
      (authorities.includes("PROVIDER_TRAINING_VIEW") || authorities.includes("PROVIDER_TRAINING_EDIT")) &&
      (authorities.includes("PROVIDER_WORK_HISTORY_VIEW") || authorities.includes("PROVIDER_WORK_HISTORY_EDIT"))
    ));

    let canRunLocationReport= (isH3Admin || (
      (authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT")) &&
      (authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT"))
    ));


    let canRunCarrierEnrollmentFormExport= (isH3Admin || (
      (authorities.includes("PROVIDER_CARRIER_ENROLLMENT_FORM_EXPORT"))
    ));

    let showLeftCol = (isH3Admin || authorities.includes("PROVIDER_SENSITIVE_DETAILS_VIEW") || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")
        || authorities.includes("PROVIDER_DEMOGRAPHICS_VIEW") || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")
        || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT"))
        || canRunDemographicDetailsReport || canRunProfileReport || canRunCvReport || canRunLocationReport || canRunCarrierEnrollmentFormExport;

    // Quick-nav targets (ids match the wrappers below); only sections this user can see
    const canSee = (permission) => isH3Admin || authorities.includes(permission + "_VIEW") || authorities.includes(permission + "_EDIT");
    const sectionNav = [
      { id: "participation-status", label: "Participation Status", show: !data.excludedFromCarrierCredentialing },
      { id: "locations", label: "Locations", show: canSee("PROVIDER_FACILITY") },
      { id: "data-hub-access", label: "Data Hub Access", show: canSee("PROVIDER_DATA_HUB") },
      { id: "documents", label: "Documents", show: canSee("PROVIDER_DOCUMENT") },
      { id: "cmes", label: "CMEs", show: canSee("PROVIDER_CME") },
      { id: "peer-references", label: "Peer References", show: canSee("PROVIDER_PEER_REFERENCE") },
      { id: "education-training", label: "Education and Training", show: canSee("PROVIDER_TRAINING") },
      { id: "work-history", label: "Work History", show: canSee("PROVIDER_WORK_HISTORY") },
      { id: "hospital-privileges", label: "Hospital Privileges", show: canSee("PROVIDER_PAST_HOSPITAL_PRIVILEGES") },
      { id: "state-licenses", label: "State Licenses", show: canSee("PROVIDER_STATE_LICENSE") },
      { id: "malpractice-insurance", label: "Malpractice Insurance", show: canSee("PROVIDER_MALPRACTICE_INSURANCE") },
      { id: "board-certifications", label: "Board Certifications", show: canSee("PROVIDER_BOARD_CERTIFICATION") },
      { id: "other-certifications", label: "Other Certifications", show: canSee("PROVIDER_OTHER_CERTIFICATION") },
      { id: "immunizations", label: "Immunizations", show: canSee("PROVIDER_IMMUNIZATION") },
      { id: "primary-source-verifications", label: "Primary Source Verifications", show: canSee("PROVIDER_PSV") },
      { id: "disclosures", label: "Disclosures", show: isH3Admin },
    ].filter((section) => section.show);

    return (
        <React.Fragment>
            <Helmet title={data.formattedName}/>
            <Container fluid className="p-0" style={{marginTop: "-20px"}}>
            <ProviderHeader
              data={data}
              onboardingStatus={<ProviderOnboardingUrl providerId={id} />}
              canEdit={isH3Admin || authorities.includes("PROVIDER_BASIC_EDIT")}
              isUploadingPhoto={isSaving && newProfilePhotoName != null}
              onEditClicked={handleEditClicked}
              onShowPracticeInfo={setInfoPractice}
              sections={sectionNav}
            />
            {infoPractice != null && <PracticeInfoModal show={true} onClose={() => setInfoPractice(null)} practice={infoPractice} />}
            <ProviderEditPanel
              show={editing}
              data={data}
              localData={localData}
              onValueChanged={onValueChanged}
              onSave={() => {handleSaveClicked(false, false)}}
              onCancel={handleCancelClicked}
              newProfilePhotoName={newProfilePhotoName}
              onProfilePhotoAdded={handleProfilePhotoAdded}
              forceSpecialtyRefresh={forceSpecialtyRefresh}
              selectedSpecialties={selectedSpecialtiesTransformed}
              onSpecialtiesChanged={handleSpecialtiesChanged}
              onSupervisingProviderStatesChanged={handleSupervisingProviderStatesChanged}
              supervisingProviderSelect={<SupervisingProviderSelect forceRefresh={forceSupervisingProviderRefresh} handleSupervisingProvidersChanged={handleSupervisingProvidersChanged} selectedSupervisingProviders={selectedSupervisingProvidersTransformed} providerId={id}/>}
            />
            <Row>
              {showLeftCol ?
              <Col xl="3">
              {(isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")) ?
              <BlockedList providerId={id}/>
              : <></>
              }
              {(isH3Admin || authorities.includes("PROVIDER_SENSITIVE_DETAILS_VIEW") || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")) ?
              <Card style={cardOutlineStyle}>
                <DetailCardHeader
                  title="Provider Details"
                  canEdit={isH3Admin || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")}
                  editing={editingDetails}
                  expanded={isExpanded}
                  onToggle={(e) => (isExpanded ? handleCollapseClicked(e) : handleExpandClicked(e))}
                  onEdit={() => {handleEditDetailsClicked()}}
                  onSave={() => {handleSaveClicked(true, false)}}
                  onCancel={() => {handleCancelClicked()}}
                />
                <Collapse in={isExpanded}>
                  <div className="px-4 pb-3">
                    <DetailSection title="Identity" first inline={!editingDetails}>
                      <DetailField label="NPI">
                        {editingDetails?
                        <EditableDetail originalValue={localData.npi} name="npi" mask="9999999999" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyText text={localData.npi} /></span>
                        }
                      </DetailField>
                      <DetailField label="DOB">
                        {editingDetails?
                        <EditableDetail originalValue={editableDobValue} name="dob" inputType="date" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyText text={dobDisplay} /></span>
                        }
                      </DetailField>
                      <DetailField label="SSN">
                        {editingDetails?
                        <EditableDetail originalValue={localData.ssn} name="ssn" mask="999-99-9999" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyText text={localData.ssn} /></span>
                        }
                      </DetailField>
                    </DetailSection>

                    <DetailSection title="Licenses & IDs" inline={!editingDetails}>
                      <DetailField label="Medical license" empty={!localData.license}>
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.license} /></span>
                      </DetailField>
                      <DetailField label="CAQH" empty={!editingDetails && !localData.caqh}>
                        {editingDetails?
                        <EditableDetail originalValue={localData.caqh} name="caqh" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.caqh} /></span>
                        }
                      </DetailField>
                      <DetailField label="DEA" empty={!localData.dea}>
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.dea} /></span>
                      </DetailField>
                      <DetailField label="Medicaid" empty={!editingDetails && !localData.medicaid}>
                        {editingDetails?
                        <EditableDetail originalValue={localData.medicaid} name="medicaid" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.medicaid} /></span>
                        }
                      </DetailField>
                      <DetailField label="PTAN" empty={!editingDetails && !localData.medicare}>
                        {editingDetails?
                        <EditableDetail originalValue={localData.medicare} name="medicare" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.medicare} /></span>
                        }
                      </DetailField>
                      <DetailField label="Other IDs" empty={!editingDetails && !localData.otherIds}>
                        {editingDetails?
                        <EditableDetail originalValue={localData.otherIds} name="otherIds" onValueChanged={onValueChanged}/>
                        :
                        <span style={{fontFamily: "monospace"}}><CopyIds value={localData.otherIds} /></span>
                        }
                      </DetailField>
                    </DetailSection>
                  </div>
                </Collapse>
                </Card>
                : <></>
                }

              {(isH3Admin || authorities.includes("PROVIDER_DEMOGRAPHICS_VIEW") || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")) ?
              <Card style={cardOutlineStyle}>
                <DetailCardHeader
                  title="Demographics"
                  canEdit={isH3Admin || authorities.includes("PROVIDER_DEMOGRAPHICS_EDIT")}
                  editing={editingDemographics}
                  expanded={isDemographicsExpanded}
                  onToggle={() => setIsDemographicsExpanded(!isDemographicsExpanded)}
                  onEdit={() => {handleEditDemographicsClicked()}}
                  onSave={() => {handleSaveClicked(false, true)}}
                  onCancel={() => {handleCancelClicked()}}
                />
                <Collapse in={isDemographicsExpanded}>
                <div className="px-4 pb-3">
                    <DetailSection title="Contact" first>
                      <DetailField label="Other phone" empty={!editingDemographics && !localData.phone2}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.phone2} mask='(999) 999-9999' name="phone2" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.phone2} />
                        }
                      </DetailField>
                      <DetailField label="Personal email" empty={!editingDemographics && !localData.email2}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.email2} name="email2" onValueChanged={onValueChanged}/>
                        :
                        <span style={{wordBreak: "break-all"}}><CopyText text={localData.email2} /></span>
                        }
                      </DetailField>
                    </DetailSection>

                    <DetailSection title="Addresses">
                      <DetailField label="Address">
                        {editingDemographics ?
                        <EditableAddress address1={localData.address1} address2={localData.address2} city={localData.city} state={localData.state} zip={localData.zip} onValueChanged={onValueChanged} />
                        :
                        <ReadOnlyAddress copyable address1={localData.address1} address2={localData.address2} city={localData.city} state={localData.state} zip={localData.zip} />
                        }
                      </DetailField>
                      <DetailField label="Prior addresses" empty={!editingDemographics && !localData.priorAddresses}>
                        {editingDemographics?
                        <EditableDetail maxLength={2048} inputType="textarea" originalValue={localData.priorAddresses} name="priorAddresses" onValueChanged={onValueChanged}/>
                        :
                        <span style={{wordBreak: "break-all"}}><CopyText text={localData.priorAddresses}>{priorAddressesReadOnly}</CopyText></span>
                        }
                      </DetailField>
                    </DetailSection>

                    <DetailSection title="Personal" inline={!editingDemographics}>
                      <DetailField label="Prior names" empty={!editingDemographics && !localData.priorNames}>
                        {editingDemographics?
                        <EditableDetail inputType="textarea" originalValue={localData.priorNames} name="priorNames" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.priorNames}>{priorNamesReadOnly}</CopyText>
                        }
                      </DetailField>
                      <DetailField label="Sex" empty={!editingDemographics && !sexReadOnly}>
                        {editingDemographics?
                        <SexSelect originalValue={localData.sex} name="sex" onValueChanged={onValueChanged} />
                        :
                        <CopyText text={sexReadOnly} />
                        }
                      </DetailField>
                      <DetailField label="Birthplace" empty={!editingDemographics && !localData.birthplace}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.birthplace} name="birthplace" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.birthplace} />
                        }
                      </DetailField>
                      <DetailField label="Citizenship" empty={!editingDemographics && !localData.citizenship}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.citizenship} name="citizenship" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.citizenship} />
                        }
                      </DetailField>
                      <DetailField label="Ethnicity" empty={!editingDemographics && !localData.ethnicity}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.ethnicity} name="ethnicity" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.ethnicity} />
                        }
                      </DetailField>
                      <DetailField label="Other languages spoken" empty={!editingDemographics && !localData.otherLanguages}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.otherLanguages} name="otherLanguages" onValueChanged={onValueChanged}/>
                        :
                        <CopyText text={localData.otherLanguages} />
                        }
                      </DetailField>
                      <DetailField label="Marital status" empty={!editingDemographics && !maritalStatusReadOnly}>
                        {editingDemographics?
                        <MaritalStatusSelect originalValue={localData.maritalStatus} name="maritalStatus" onValueChanged={onValueChanged} />
                        :
                        <CopyText text={maritalStatusReadOnly} />
                        }
                      </DetailField>
                      <DetailField label="Spouse" empty={!editingDemographics && !localData.spouse}>
                        {editingDemographics?
                        <EditableDetail originalValue={localData.spouse} name="spouse" onValueChanged={onValueChanged}/>
                        :
                        localData.spouse
                        }
                      </DetailField>
                    </DetailSection>
                </div>
                </Collapse>
                </Card>
                : <></>
                }
                {(canRunDemographicDetailsReport || canRunProfileReport || canRunCvReport || canRunLocationReport) &&
                <Card style={cardOutlineStyle}>
                  <DetailCardHeader title="Exports" expanded={isExportsExpanded} onToggle={() => setIsExportsExpanded(!isExportsExpanded)} />
                  <Collapse in={isExportsExpanded}>
                  <div className="px-4 pb-3">
                    <Table className="table-sm align-middle mb-0" striped hover>
                      <tbody>
                        {canRunDemographicDetailsReport && <ExportRow label="Demographic Details" busy={isExportingDemographicDetails} onClick={handleExportDemographicDetailsClicked} />}
                        {canRunProfileReport && <ExportRow label="Profile Report" busy={isExportingProfile} onClick={handleExportProfileClicked} />}
                        {canRunCvReport && <ExportRow label="Provider CV" busy={isExportingCv} onClick={handleExportCvClicked} />}
                        {canRunLocationReport && <ExportRow label="Active Locations" busy={isExportingLocations} onClick={handleExportLocationsClicked} />}
                      </tbody>
                    </Table>
                  </div>
                  </Collapse>
                </Card>
                }
                {canRunCarrierEnrollmentFormExport &&
                  <CarrierEnrollmentForms />
                }
                {(isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")) ?
                    <OpenTasks entityType="provider" entityId={id} practiceIds={data.practices ? data.practices.map((practice) => practice.id) : undefined} />
                  : <></>
                }
              </Col>
              : <></>
              }
              <Col xl={showLeftCol ? "9" : "12"}>
                {!data.excludedFromCarrierCredentialing &&
                <div id="participation-status" className="provider-section"><CarrierParticipationStatusTable providerId={id} providerName={data.formattedName} practices={data.practices} /></div>
                }

                {(isH3Admin || authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")) ?
                <div id="locations" className="provider-section"><FacilityParticipationStatusTable providerId={id} providerName={data.formattedName} canEdit={isH3Admin || authorities.includes("PROVIDER_FACILITY_EDIT")}/></div>
                : <></>
                }
               
                {(isH3Admin || authorities.includes("PROVIDER_DATA_HUB_VIEW") || authorities.includes("PROVIDER_DATA_HUB_EDIT")) ?
                <div id="data-hub-access" className="provider-section"><HubParticipationStatusTable entityType="provider" entityId={id} entityName={data.formattedName} canEdit={isH3Admin || authorities.includes("PROVIDER_DATA_HUB_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")) ?
                <div id="documents" className="provider-section"><Card style={cardOutlineStyle}>
                  <Card.Header style={{marginBottom: '-25px'}}>
                    <Card.Title style={{marginBottom: '-15px'}}>Documents</Card.Title>
                    <br />
                  </Card.Header>
                  <Card.Body style={{overflowY: 'scroll', maxHeight: '700px'}}>
                    <DocumentList entityId={id} entityType="provider" canEdit={isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT")} />
                  </Card.Body>
                </Card></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_CME_VIEW") || authorities.includes("PROVIDER_CME_EDIT")) ?
                  <div id="cmes" className="provider-section"><ProviderCME providerFormattedName={data.formattedName} providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_CME_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_PEER_REFERENCE_VIEW") || authorities.includes("PROVIDER_PEER_REFERENCE_EDIT")) ?
                  <div id="peer-references" className="provider-section"><PeerReferences providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_PEER_REFERENCE_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_TRAINING_VIEW") || authorities.includes("PROVIDER_TRAINING_EDIT")) ?
                  <div id="education-training" className="provider-section"><Trainings providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_TRAINING_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_WORK_HISTORY_VIEW") || authorities.includes("PROVIDER_WORK_HISTORY_EDIT")) ?
                  <div id="work-history" className="provider-section"><WorkHistories providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_WORK_HISTORY_EDIT")}/></div>
                : <></>
                }
                
                {(isH3Admin || authorities.includes("PROVIDER_PAST_HOSPITAL_PRIVILEGES_VIEW") || authorities.includes("PROVIDER_PAST_HOSPITAL_PRIVILEGES_EDIT")) ?
                  <div id="hospital-privileges" className="provider-section"><PastHospitalPrivilegesList providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_PAST_HOSPITAL_PRIVILEGES_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_STATE_LICENSE_VIEW") || authorities.includes("PROVIDER_STATE_LICENSE_EDIT")) ?
                  <div id="state-licenses" className="provider-section"><StateLicenses providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_STATE_LICENSE_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_MALPRACTICE_INSURANCE_VIEW") || authorities.includes("PROVIDER_MALPRACTICE_INSURANCE_EDIT")) ?
                  <div id="malpractice-insurance" className="provider-section"><MalpracticeInsurances providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_MALPRACTICE_INSURANCE_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_BOARD_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT")) ?
                  <div id="board-certifications" className="provider-section"><BoardCertifications providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_OTHER_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_OTHER_CERTIFICATION_EDIT")) ?
                  <div id="other-certifications" className="provider-section"><OtherCertifications providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_OTHER_CERTIFICATION_EDIT")}/></div>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_IMMUNIZATION_VIEW") || authorities.includes("PROVIDER_IMMUNIZATION_EDIT")) ?
                  <div id="immunizations" className="provider-section"><Immunizations providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_IMMUNIZATION_EDIT")}/></div>
                : <></>
                }

                {(localData.showAreasOfExpertise && (isH3Admin || authorities.includes("PROVIDER_AREA_OF_EXPERTISE_VIEW") || authorities.includes("PROVIDER_AREA_OF_EXPERTISE_EDIT"))) ?
                  <AreasOfExpertise providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_AREA_OF_EXPERTISE_EDIT")}/>
                : <></>
                }

                {(localData.showTreatmentModalities && (isH3Admin || authorities.includes("PROVIDER_TREATMENT_MODALITY_VIEW") || authorities.includes("PROVIDER_TREATMENT_MODALITY_EDIT"))) ?
                  <TreatmentModalities providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_TREATMENT_MODALITY_EDIT")}/>
                : <></>
                }

                {(isH3Admin || authorities.includes("PROVIDER_PSV_VIEW") || authorities.includes("PROVIDER_PSV_EDIT")) ?
                  <div id="primary-source-verifications" className="provider-section"><Psvs providerId={id} canEdit={isH3Admin || authorities.includes("PROVIDER_PSV_EDIT")}/></div>
                : <></>
                }

                {isH3Admin ?
                  <div id="disclosures" className="provider-section"><Disclosures providerId={id} /></div>
                : <></>
                }
              </Col>
            </Row>
            </Container>
        </React.Fragment>
    );
  }
};

export default Provider;
